<script setup lang="ts">
  import { computed, ref, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useCart } from '@/composables/useCart';
  import { useCartStore } from '@/stores/cart';
  import { useAuthStore } from '@/stores/auth';
  import { useWishlistStore } from '@/stores/wishlist';
  import PublicNavbar from '@/sites/tfs/components/layout/PublicNavbar.vue';
  import SearchTrigger from '@/sites/tfs/components/public/SearchTrigger.vue';
  import SearchModal from '@/sites/tfs/components/public/SearchModal.vue';
  import PublicMobileMenu from '@/sites/tfs/components/layout/PublicMobileMenu.vue';
  import PublicUserMenu from '@/sites/tfs/components/layout/PublicUserMenu.vue';
  import PublicCartIcon from '@/sites/tfs/modules/cart/components/PublicCartIcon.vue';
  import LanguageSwitcher from '@/sites/tfs/components/layout/LanguageSwitcher.vue';
  import PublicCartSidebar from '@/sites/tfs/modules/cart/components/PublicCartSidebar.vue';
  import ToastContainer from '@/sites/tfs/components/common/ToastContainer.vue';
  import BaseButton from '@/lib/ui/components/BaseButton.vue';
  import RecoverableOrdersNotice from '@/sites/tfs/components/layout/RecoverableOrdersNotice.vue';
  import NotificationIcon from '@/sites/tfs/components/notifications/NotificationIcon.vue';
  import { getSiteSettings } from '@/modules/seo/siteSettings';

  const authStore = useAuthStore();
  const { t } = useI18n();
  const cartStore = useCartStore();
  const wishlistStore = useWishlistStore();
  const {
     items,
     itemsCount,
     loading,
     subtotal,
     discountAmount,
     total,
     loadCart,
     updateQuantity,
     removeFromCart
  } = useCart();

  const isCartOpen = ref(false);
  const isSearchModalOpen = ref(false);
  const isMobileMenuOpen = ref(false);
  const isClientMounted = ref(false);
  const siteTitle = computed(() => getSiteSettings().site_title || getSiteSettings().organization_name);

  const handleLogin = async () => {
    isMobileMenuOpen.value = false;
    await authStore.initiateSilentOAuthLogin();
  };

  onMounted(() => {
    isClientMounted.value = true;
    cartStore.init(); // Restore from LocalStorage
    loadCart();      // Sync with API
    wishlistStore.init(); // Initialize wishlist
  });
</script>

<template>
  <div class="min-h-screen bg-noir-950 flex flex-col relative">
    <!-- Gradient mesh background -->
    <div class="fixed inset-0 pointer-events-none">
      <div class="absolute inset-0 bg-[url('@/assets/textures/gradient-mesh.svg')] opacity-30"></div>
    </div>

    <!-- Header con navegación - Glassmorphism -->
    <header class="sticky top-0 z-40 bg-noir-950/80 backdrop-blur-lg border-b border-noir-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0 md:py-3">
        <div class="h-16 flex items-center justify-between">
          <div class="flex items-center gap-8">
          <!-- Logo -->
          <router-link to="/" class="text-2xl font-display font-bold text-gold-400 tracking-tight hover:text-gold-300 transition-colors">
            TFS
          </router-link>

          <!-- Navegación principal -->
          <PublicNavbar />
          </div>

          <div class="hidden md:flex flex-1 justify-center px-4">
            <SearchTrigger @click="isSearchModalOpen = true" />
          </div>

          <!-- Acciones de usuario -->
          <div class="flex items-center gap-1 md:gap-4">
            <button
              type="button"
              class="md:hidden inline-flex h-10 w-10 items-center justify-center text-cream-100 hover:text-gold-400 transition-colors"
              :aria-label="t('nav.search')"
              @click="isSearchModalOpen = true"
            >
              <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <circle cx="11" cy="11" r="7" stroke-width="2" />
                <path d="M20 20l-3.5-3.5" stroke-width="2" stroke-linecap="round" />
              </svg>
            </button>

            <div class="hidden md:block">
              <LanguageSwitcher />
            </div>

            <PublicCartIcon
              :count="itemsCount"
              @click="isCartOpen = true"
            />

            <NotificationIcon />

            <!-- Usuario autenticado: menú -->
            <div class="hidden md:block">
              <PublicUserMenu v-if="authStore.isAuthenticated" />

              <!-- No autenticado: botón login -->
              <BaseButton
                v-else
                variant="primary"
                size="sm"
                @click="handleLogin"
              >
                {{ t('nav.login') }}
              </BaseButton>
            </div>

            <!-- Menú móvil: botón hamburguesa / X -->
            <button
              type="button"
              class="md:hidden inline-flex h-10 w-10 items-center justify-center text-cream-100 hover:text-gold-400 transition-colors"
              :aria-label="isMobileMenuOpen ? t('nav.menu_close') : t('nav.menu_open')"
              :aria-expanded="isMobileMenuOpen"
              aria-controls="public-mobile-menu"
              @click="isMobileMenuOpen = !isMobileMenuOpen"
            >
              <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <path v-if="isMobileMenuOpen" d="M6 6l12 12M18 6L6 18" />
                <path v-else d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          </div>
        </div>

      </div>
    </header>

    <PublicMobileMenu
      v-if="isMobileMenuOpen"
      @close="isMobileMenuOpen = false"
      @login="handleLogin"
    />

    <RecoverableOrdersNotice />

    <!-- Main Content -->
    <main class="flex-1 relative z-10">
      <slot />
    </main>

    <!-- Footer elegante -->
    <footer class="border-t border-noir-800 py-12 mt-auto relative z-10 bg-noir-950/50 backdrop-blur">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col md:flex-row justify-between items-center gap-4">
          <div class="text-center md:text-left">
            <h3 class="font-display text-xl text-gold-400 mb-1">{{ siteTitle }}</h3>
            <p class="text-noir-300 text-sm">Capturing moments, creating art</p>
          </div>
          <div class="text-noir-400 text-sm font-sans">
            © {{ new Date().getFullYear() }} The Forbidden Studio. All rights reserved.
          </div>
        </div>
      </div>
    </footer>

    <!-- Cart Sidebar -->
    <PublicCartSidebar 
      v-if="isClientMounted"
      :is-open="isCartOpen"
      :items="items"
      :loading="loading"
      :subtotal="subtotal"
      :discount-amount="discountAmount"
      :total="total"
      @close="isCartOpen = false"
      @update:quantity="updateQuantity"
      @remove="removeFromCart"
    />

    <!-- Toasts -->
    <ToastContainer />

    <SearchModal v-if="isClientMounted" v-model="isSearchModalOpen" />
  </div>
</template>
